import type { JSX } from 'react';

import { DateTime } from 'luxon';
import { create, type StoreApi, type UseBoundStore } from 'zustand';

import type { DepositBanditMethodSuggestions, DepositBanditPaymentMethod } from 'models/reqres';
import type { TransferVo } from 'models/transfer';
import type { DepositTrackingContext } from 'utils/depositTracking';
import { trackFirstDepositIfLeaving } from 'utils/logrocketFirstDeposit';

export type DepositDialogStep =
    | 'prereview'
    | 'review'
    | 'initiated'
    | 'failed'
    | 'kyc'
    | 'debit-card'
    | 'stripe-card'
    | 'billing-address'
    | 'braintree-paypal'
    | 'braintree-venmo'
    | 'stripe-crypto'
    | 'partial-funding';

/**
 * A partial authorization waiting on the user, plus how to finish the flow that
 * found it.
 *
 * The callbacks are held here for the same reason `identityFlowDialogStore`
 * holds one: the prompt is a step of the shared dialog, but the work after it
 * belongs to the flow that started the deposit (its own analytics, its own
 * success surface), and that flow is unmounted while the prompt is on screen.
 */
export interface PartialFundingPrompt {
    depositId: string;
    authorizedAmountCents: number;
    requestedAmountCents: number;
    /** Runs after the capture succeeds, with the amount the server captured. */
    onAccepted: (capturedAmountCents: number) => void;
    /** Runs after the hold is released, before the user picks another method. */
    onRejected?: () => void;
}

type DepositSuggestionKey = `web:${DepositBanditPaymentMethod}`;

export const getDepositSuggestionKey = (paymentMethod: DepositBanditPaymentMethod): DepositSuggestionKey =>
    `web:${paymentMethod}`;

const defaultErrorContinueText = 'Other funding options';
const initialStep: DepositDialogStep = 'prereview';

interface DepositDialogState {
    step: DepositDialogStep;
    eventTicker: string | undefined;
    lastInitiatedTime: DateTime;
    amount: number;
    fee: number;
    errorTitle: string | JSX.Element | undefined;
    errorMessage: string | JSX.Element | undefined;
    errorContinueText: string;
    errorCode: string | undefined;
    deposit: TransferVo | undefined;
    // Amount actually credited, resolved after settlement. Stripe crypto can settle
    // for less than requested (underfunding), so the entered amount may overstate it.
    settledAmountCents: number | null;
    partialFunding: PartialFundingPrompt | null;
    resultViewedTrackingContext: DepositTrackingContext | undefined;
    resumeExistingFlowOnNextOpen: boolean;
    loading: boolean;
    bankId: string;
    cardId: string;
    braintreePaymentMethodId: string | undefined;
    billingAddressLineOne: string;
    billingAddressLineTwo: string;
    billingCity: string;
    billingState: string;
    billingZipCode: string;
    billingCountry: string;
    depositSuggestions: Map<DepositSuggestionKey, DepositBanditMethodSuggestions>;
    activeSuggestionKey: DepositSuggestionKey | null;
    actions: {
        setStep: (step: DepositDialogStep) => void;
        setInitialStep: () => void;
        getPreviousStep: () => DepositDialogStep | undefined;
        goToPreviousStep: () => void;
        setAmount: (amount: number) => void;
        setFee: (fee: number) => void;
        setErrorTitle: (title: string | JSX.Element | undefined) => void;
        setErrorMessage: (message: string | JSX.Element | undefined) => void;
        setErrorContinueText: (text: string) => void;
        setErrorCode: (code: string | undefined) => void;
        setDeposit: (deposit: TransferVo | undefined) => void;
        setSettledAmountCents: (cents: number | null) => void;
        showPartialFunding: (prompt: PartialFundingPrompt) => void;
        clearPartialFunding: () => void;
        setResultViewedTrackingContext: (context: DepositTrackingContext | undefined) => void;
        clearResultViewedTrackingContext: () => void;
        markResumeExistingFlowOnNextOpen: () => void;
        consumeResumeExistingFlowOnNextOpen: () => boolean;
        setBillingAddress: (
            lineOne: string,
            lineTwo: string,
            city: string,
            state: string,
            zip: string,
            country: string,
        ) => void;
        setBankId: (id: string) => void;
        setCardId: (id: string) => void;
        setBraintreePaymentMethodId: (id: string | undefined) => void;
        setDepositSuggestion: (key: DepositSuggestionKey, suggestion: DepositBanditMethodSuggestions) => void;
        setActiveSuggestionKey: (key: DepositSuggestionKey | null) => void;
        clearDepositSuggestions: () => void;
        activeDepositSuggestion: () => DepositBanditMethodSuggestions | undefined;
        setEventTicker: (ticker: string | undefined) => void;
        reset: () => void;
    };
}

const initialState: Omit<DepositDialogState, 'actions'> = {
    step: initialStep,
    eventTicker: undefined,
    lastInitiatedTime: DateTime.now(),
    amount: 0,
    fee: 0,
    errorTitle: undefined,
    errorMessage: undefined,
    errorContinueText: defaultErrorContinueText,
    errorCode: undefined,
    deposit: undefined,
    settledAmountCents: null,
    partialFunding: null,
    resultViewedTrackingContext: undefined,
    resumeExistingFlowOnNextOpen: false,
    loading: false,
    bankId: '',
    cardId: '',
    braintreePaymentMethodId: undefined,
    billingAddressLineOne: '',
    billingAddressLineTwo: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',
    billingCountry: 'US',
    depositSuggestions: new Map(),
    activeSuggestionKey: null,
};

export const useDepositDialogStore: UseBoundStore<StoreApi<DepositDialogState>> = create<DepositDialogState>()(
    (set) => ({
        ...initialState,
        actions: {
            setStep: (step) => {
                const currentStep = useDepositDialogStore.getState().step;
                if (currentStep === 'prereview' && step !== 'prereview') {
                    trackFirstDepositIfLeaving();
                }
                set({
                    step,
                    ...(currentStep === 'initiated' ? { lastInitiatedTime: DateTime.now() } : {}),
                });
            },
            setInitialStep: () => set({ step: initialStep }),
            getPreviousStep: () => {
                const { step } = useDepositDialogStore.getState();
                switch (step) {
                    case 'review':
                    case 'kyc':
                    case 'billing-address':
                    case 'stripe-card':
                    case 'braintree-paypal':
                    case 'braintree-venmo':
                        return 'prereview';
                    case 'debit-card':
                        return 'billing-address';
                    default:
                        return undefined;
                }
            },
            goToPreviousStep: () => {
                const prev = useDepositDialogStore.getState().actions.getPreviousStep();
                if (prev) useDepositDialogStore.getState().actions.setStep(prev);
            },
            setAmount: (amount) => set({ amount }),
            setFee: (fee) => set({ fee }),
            setErrorTitle: (errorTitle) => set({ errorTitle }),
            setErrorMessage: (errorMessage) => set({ errorMessage }),
            setErrorContinueText: (errorContinueText) => set({ errorContinueText }),
            setErrorCode: (errorCode) => set({ errorCode }),
            setDeposit: (deposit) => set({ deposit }),
            setSettledAmountCents: (settledAmountCents) => set({ settledAmountCents }),
            showPartialFunding: (partialFunding) => set({ partialFunding, step: 'partial-funding' }),
            // Leaves the step alone: the caller decides where the flow goes next
            // (its success screen, the method list, or a closed dialog).
            clearPartialFunding: () => set({ partialFunding: null }),
            setResultViewedTrackingContext: (resultViewedTrackingContext) => set({ resultViewedTrackingContext }),
            clearResultViewedTrackingContext: () => set({ resultViewedTrackingContext: undefined }),
            markResumeExistingFlowOnNextOpen: () => set({ resumeExistingFlowOnNextOpen: true }),
            consumeResumeExistingFlowOnNextOpen: () => {
                const shouldResume = useDepositDialogStore.getState().resumeExistingFlowOnNextOpen;
                set({ resumeExistingFlowOnNextOpen: false });
                return shouldResume;
            },
            setBillingAddress: (lineOne, lineTwo, city, state, zip, country) =>
                set({
                    billingAddressLineOne: lineOne,
                    billingAddressLineTwo: lineTwo,
                    billingCity: city,
                    billingState: state,
                    billingZipCode: zip,
                    billingCountry: country,
                }),
            setBankId: (bankId) => set({ bankId }),
            setCardId: (cardId) => set({ cardId }),
            setBraintreePaymentMethodId: (braintreePaymentMethodId) => set({ braintreePaymentMethodId }),
            setDepositSuggestion: (key, suggestion) =>
                set((state) => {
                    const newMap = new Map(state.depositSuggestions);
                    newMap.set(key, suggestion);
                    return { depositSuggestions: newMap };
                }),
            setActiveSuggestionKey: (activeSuggestionKey) => set({ activeSuggestionKey }),
            clearDepositSuggestions: () => set({ depositSuggestions: new Map(), activeSuggestionKey: null }),
            activeDepositSuggestion: () => {
                const { activeSuggestionKey, depositSuggestions } = useDepositDialogStore.getState();
                return activeSuggestionKey ? depositSuggestions.get(activeSuggestionKey) : undefined;
            },
            setEventTicker: (eventTicker) => set({ eventTicker }),
            reset: () =>
                set({
                    step: initialStep,
                    amount: 0,
                    fee: 0,
                    errorTitle: undefined,
                    errorMessage: undefined,
                    errorCode: undefined,
                    errorContinueText: defaultErrorContinueText,
                    deposit: undefined,
                    settledAmountCents: null,
                    partialFunding: null,
                    resultViewedTrackingContext: undefined,
                    resumeExistingFlowOnNextOpen: false,
                    bankId: '',
                    cardId: '',
                    braintreePaymentMethodId: undefined,
                    billingAddressLineOne: '',
                    billingAddressLineTwo: '',
                    billingCity: '',
                    billingState: '',
                    billingZipCode: '',
                    billingCountry: 'US',
                }),
        },
    }),
);
